import React, { useState, useEffect, useRef } from "react";
import { Spin, Typography, Button, Slider, Space, Tooltip } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  ExpandOutlined,
  DragOutlined,
} from "@ant-design/icons";
import trans from "../../utils";

const { Text } = Typography;

/**
 * ImageMapViewer Component - ENHANCED VERSION
 * Displays an image map with clickable location pins
 * Features:
 * - Auto-centered image
 * - Double-click to pan
 * - Shift+drag to pan
 * - Smooth zoom with mouse wheel
 * - Better visual feedback
 */
const ImageMapViewer = ({
  imageUrl,
  imageWidth,
  imageHeight,
  rooms = [],
  selectedRoomCode = null,
  onRoomClick,
  onRoomDoubleClick,
  loading = false,
  isMobile = false,
}) => {
  const [scale, setScale] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [fitScale, setFitScale] = useState(1);
  const [viewportSize, setViewportSize] = useState({
    width: 1100,
    height: 600,
  });
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const mapContainerRef = useRef(null);
  const touchStartRef = useRef({ pan: null, pinch: null });

  // Fixed viewport dimensions (desktop)
  const VIEWPORT_WIDTH = isMobile ? viewportSize.width : 1100;
  const VIEWPORT_HEIGHT = isMobile ? viewportSize.height : 600;

  // Measure container on mobile for responsive viewport
  useEffect(() => {
    if (!isMobile || !containerRef.current) return;
    const el = containerRef.current;
    const updateSize = () => {
      if (!el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      // Avoid collapsing to zero (causes image to render as a dot); use minimum or skip update
      const safeW = w > 0 ? w : 300;
      const safeH = h > 0 ? h : 280;
      setViewportSize({ width: safeW, height: safeH });
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile]);

  // Non-passive touchmove so we can preventDefault when panning/pinching (stops modal scroll)
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el || !isMobile) return;
    const onTouchMove = (e) => {
      if (e.touches.length === 1 && touchStartRef.current.pan) {
        e.preventDefault();
      } else if (e.touches.length === 2 && touchStartRef.current.pinch) {
        e.preventDefault();
      }
    };
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, [isMobile]);

  // Reset pan and scale when image URL changes
  useEffect(() => {
    if (imageUrl) {
      setPanOffset({ x: 0, y: 0 });
      setImageLoaded(false);
    }
  }, [imageUrl]);

  // Calculate scale to fit container and center the image
  useEffect(() => {
    if (!imageWidth || !imageHeight || !imageLoaded) return;
    const w = VIEWPORT_WIDTH - (isMobile ? 16 : 40);
    const h = VIEWPORT_HEIGHT - (isMobile ? 16 : 80);
    if (w <= 0 || h <= 0) return;

    const scaleX = w / imageWidth;
    const scaleY = h / imageHeight;
    const newFitScale = Math.min(scaleX, scaleY, 1);

    setFitScale(newFitScale);
    setScale(newFitScale);

    // Center the image initially
    centerImage(newFitScale);
  }, [
    imageWidth,
    imageHeight,
    imageLoaded,
    VIEWPORT_WIDTH,
    VIEWPORT_HEIGHT,
    isMobile,
  ]);

  // ✅ NEW: Center the image in the viewport
  const centerImage = (currentScale = scale) => {
    const scaledWidth = imageWidth * currentScale;
    const scaledHeight = imageHeight * currentScale;

    // Calculate offsets to center the image
    const offsetX = (VIEWPORT_WIDTH - scaledWidth) / 2;
    const offsetY = (VIEWPORT_HEIGHT - scaledHeight) / 2;

    // Only apply centering offset if image is smaller than viewport
    setPanOffset({
      x: Math.max(0, offsetX),
      y: Math.max(0, offsetY),
    });
  };

  // Handle shift key for panning
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Shift") {
        setIsShiftPressed(true);
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === "Shift") {
        setIsShiftPressed(false);
        setIsDragging(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // ✅ ENHANCED: Mouse drag handlers (works with both shift and without)
  const handleMouseDown = (e) => {
    // Allow drag with shift OR when zoomed in
    if ((isShiftPressed || scale > fitScale) && e.button === 0) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panOffset.x,
        y: e.clientY - panOffset.y,
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPanOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile: single-finger pan, two-finger pinch zoom
  const handleTouchStart = (e) => {
    if (!imageLoaded || e.touches.length === 0) return;
    if (e.touches.length === 1) {
      touchStartRef.current.pan = {
        startClientX: e.touches[0].clientX,
        startClientY: e.touches[0].clientY,
        startPanX: panOffset.x,
        startPanY: panOffset.y,
      };
      touchStartRef.current.pinch = null;
    } else if (e.touches.length === 2) {
      const d = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      touchStartRef.current.pinch = {
        distance: d,
        scale,
      };
      touchStartRef.current.pan = null;
    }
  };

  const handleTouchMove = (e) => {
    if (!imageLoaded || !isMobile) return;
    if (e.touches.length === 1 && touchStartRef.current.pan) {
      e.preventDefault();
      const p = touchStartRef.current.pan;
      setPanOffset({
        x: p.startPanX + (e.touches[0].clientX - p.startClientX),
        y: p.startPanY + (e.touches[0].clientY - p.startClientY),
      });
    } else if (e.touches.length === 2 && touchStartRef.current.pinch) {
      e.preventDefault();
      const d = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      const ratio = d / touchStartRef.current.pinch.distance;
      const newScale = Math.max(
        fitScale,
        Math.min(3, touchStartRef.current.pinch.scale * ratio),
      );
      setScale(newScale);
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length === 0) {
      touchStartRef.current.pan = null;
      touchStartRef.current.pinch = null;
    }
  };

  // ✅ NEW: Double-click to pan to that location
  const handleDoubleClick = (e) => {
    if (!imageLoaded) return;

    const rect = mapContainerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Calculate the center of the viewport
    const centerX = VIEWPORT_WIDTH / 2;
    const centerY = VIEWPORT_HEIGHT / 2;

    // Calculate offset to move clicked point to center
    const deltaX = centerX - clickX;
    const deltaY = centerY - clickY;

    setPanOffset((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
  };

  // // ✅ NEW: Mouse wheel zoom
  // const handleWheel = (e) => {
  //   if (!imageLoaded) return;

  //   e.preventDefault();
  //   const delta = e.deltaY > 0 ? -0.1 : 0.1;
  //   const newScale = Math.max(fitScale, Math.min(3, scale + delta));

  //   if (newScale !== scale) {
  //     setScale(newScale);
  //     // Recenter if zooming out to fit scale
  //     if (newScale === fitScale) {
  //       centerImage(newScale);
  //     }
  //   }
  // };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale - 0.2, fitScale);
    setScale(newScale);
    if (newScale === fitScale) {
      centerImage(newScale);
    }
  };

  // ✅ NEW: Reset view (fit and center)
  const handleResetView = () => {
    setScale(fitScale);
    centerImage(fitScale);
  };

  const handleZoomChange = (value) => {
    const newScale = value / 100;
    const finalScale = Math.max(newScale, fitScale);
    setScale(finalScale);
    if (finalScale === fitScale) {
      centerImage(finalScale);
    }
  };

  const handleRoomClick = (room, e) => {
    e.stopPropagation(); // Prevent double-click pan when clicking room
    if (onRoomClick) {
      onRoomClick(room, e);
    }
  };

  const handleRoomDoubleClick = (room, e) => {
    e.stopPropagation(); // Prevent double-click pan when double-clicking room
    if (onRoomDoubleClick) {
      onRoomDoubleClick(room, e);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (!imageUrl) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          height: VIEWPORT_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text type="secondary">{trans("No map image available")}</Text>
      </div>
    );
  }

  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;

  return (
    <div
      ref={containerRef}
      style={{
        width: isMobile ? "100%" : VIEWPORT_WIDTH,
        height: isMobile ? "100%" : VIEWPORT_HEIGHT,
        minWidth: isMobile ? 0 : undefined,
        minHeight: isMobile ? 0 : undefined,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        backgroundColor: "#fafafa",
        border: "2px solid #d9d9d9",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      {/* Enhanced Zoom Controls */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          zIndex: 100,
          backgroundColor: "white",
          padding: "12px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <Space direction="vertical" size="small" align="center">
          <Tooltip title={trans("Zoom In")} placement="left">
            <Button
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              size="small"
              disabled={scale >= 3}
              type="text"
            />
          </Tooltip>
          <Slider
            vertical
            min={Math.round(fitScale * 100)}
            max={300}
            value={scale * 100}
            onChange={handleZoomChange}
            style={{ height: "120px", margin: "8px 0" }}
            tooltip={{
              formatter: (value) => `${value}%`,
            }}
          />
          <Tooltip title={trans("Zoom Out")} placement="left">
            <Button
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              size="small"
              disabled={scale <= fitScale}
              type="text"
            />
          </Tooltip>
          <div
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#e8e8e8",
              margin: "4px 0",
            }}
          />
          <Tooltip title={trans("Reset View")} placement="left">
            <Button
              icon={<ExpandOutlined />}
              onClick={handleResetView}
              size="small"
              type="text"
            />
          </Tooltip>
        </Space>
      </div>

      {/* Enhanced Instructions */}
      {!isMobile && (
        <div
          style={{
            position: "absolute",
            top: "16px",
            left: "16px",
            zIndex: 100,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            color: "white",
            padding: "12px 16px",
            borderRadius: "8px",
            fontSize: "13px",
            maxWidth: "300px",
            lineHeight: "1.6",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <DragOutlined />
            {trans("Navigation Tips")}
          </div>
          <div style={{ fontSize: "12px", opacity: 0.9 }}>
            • {trans("Double-click to center view")}
            <br />• {trans("Shift + Drag to pan")}
            <br />
            {/* • {trans("Mouse wheel to zoom")}<br /> */}•{" "}
            {trans("Click pins to select location")}
          </div>
        </div>
      )}

      {/* Zoom Level Indicator */}
      <div
        style={{
          position: "absolute",
          bottom: "16px",
          right: "16px",
          zIndex: 100,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          padding: "6px 12px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "bold",
        }}
      >
        {Math.round(scale * 100)}%
      </div>

      {/* Map Container */}
      <div
        ref={mapContainerRef}
        style={{
          width: isMobile ? "100%" : VIEWPORT_WIDTH,
          height: isMobile ? "100%" : VIEWPORT_HEIGHT,
          flex: isMobile ? 1 : undefined,
          minHeight: isMobile ? 0 : undefined,
          overflow: "hidden",
          position: "relative",
          cursor: isDragging
            ? "grabbing"
            : isShiftPressed || scale > fitScale
              ? "grab"
              : "default",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        // onWheel={handleWheel}
      >
        <Spin
          spinning={loading || !imageLoaded}
          tip={trans("Loading map...")}
          size="large"
        >
          <div
            style={{
              position: "absolute",
              left: panOffset.x,
              top: panOffset.y,
              transition: isDragging ? "none" : "transform 0.2s ease-out",
              width: scaledWidth,
              height: scaledHeight,
            }}
          >
            {/* Base Image */}
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Map"
              onLoad={handleImageLoad}
              style={{
                width: scaledWidth,
                height: scaledHeight,
                display: "block",
                userSelect: "none",
                pointerEvents: "none",
                borderRadius: "4px",
              }}
            />

            {/* Location Pins Overlay */}
            {imageLoaded &&
              rooms.map((room) => {
                const isSelected =
                  selectedRoomCode === room.code ||
                  selectedRoomCode === room.poiId;
                const scaledX = room.x * scale;
                const scaledY = room.y * scale;

                const pinX = room.textPosition
                  ? room.textPosition.x * scale
                  : scaledX + (room.width * scale) / 2;
                const pinY = room.textPosition
                  ? room.textPosition.y * scale
                  : scaledY + (room.height * scale) / 2;

                return (
                  <div
                    key={room.code || room.poiId || room.id}
                    onClick={(e) => handleRoomClick(room, e)}
                    onDoubleClick={(e) => handleRoomDoubleClick(room, e)}
                    style={{
                      position: "absolute",
                      left: `${pinX}px`,
                      top: `${pinY}px`,
                      transform: "translate(-50%, -100%)",
                      cursor: "pointer",
                      zIndex: isSelected ? 20 : 10,
                      transition: "all 0.3s ease",
                    }}
                    title={room.displayName || room.code || room.name}
                  >
                    {/* Enhanced Location Pin */}
                    <div
                      style={{
                        width: isSelected ? "36px" : "28px",
                        height: isSelected ? "36px" : "28px",
                        backgroundColor: isSelected ? "#52c41a" : "#1890ff",
                        borderRadius: "50% 50% 50% 0",
                        transform: "rotate(-45deg)",
                        border: `3px solid ${
                          isSelected ? "#389e0d" : "#096dd9"
                        }`,
                        boxShadow: isSelected
                          ? "0 0 20px rgba(82, 196, 26, 0.9), 0 4px 12px rgba(0,0,0,0.3)"
                          : "0 4px 8px rgba(0,0,0,0.25)",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        animation: isSelected ? "pulse 2s infinite" : "none",
                      }}
                      className="location-pin"
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.width = "32px";
                          e.currentTarget.style.height = "32px";
                          e.currentTarget.style.backgroundColor = "#40a9ff";
                          e.currentTarget.style.transform =
                            "rotate(-45deg) scale(1.1)";
                          e.currentTarget.style.boxShadow =
                            "0 0 12px rgba(24, 144, 255, 0.8), 0 4px 12px rgba(0,0,0,0.3)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.width = "28px";
                          e.currentTarget.style.height = "28px";
                          e.currentTarget.style.backgroundColor = "#1890ff";
                          e.currentTarget.style.transform =
                            "rotate(-45deg) scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 8px rgba(0,0,0,0.25)";
                        }
                      }}
                    >
                      {/* Inner dot */}
                      <div
                        style={{
                          width: isSelected ? "10px" : "8px",
                          height: isSelected ? "10px" : "8px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                          transform: "rotate(45deg)",
                          transition: "all 0.3s ease",
                        }}
                      />
                    </div>

                    {/* Enhanced Room Label */}
                    <div
                      style={{
                        position: "absolute",
                        top: "-32px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: isSelected ? "#52c41a" : "#1890ff",
                        color: "white",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: `${Math.max(11, scale * 12)}px`,
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        pointerEvents: "none",
                        border: `2px solid ${
                          isSelected ? "#389e0d" : "#096dd9"
                        }`,
                      }}
                    >
                      {room.displayName || room.code}
                    </div>
                  </div>
                );
              })}
          </div>
        </Spin>
      </div>

      {/* CSS Animation for selected pin */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 0 20px rgba(82, 196, 26, 0.9), 0 4px 12px rgba(0,0,0,0.3);
            }
            50% {
              box-shadow: 0 0 30px rgba(82, 196, 26, 1), 0 4px 12px rgba(0,0,0,0.3);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ImageMapViewer;
